// Resolves a notification's `action_url` into something the SPA router can
// actually navigate to.
//
// Backend producers emit action URLs in three shapes, and the convention is
// that the SPA resolves the org context client-side (see the comments on
// notify.KindAssistantSuggestion in internal/services/automation/sweep.go and
// notify.KindInvoiceReminderRunDone in internal/jobs/billing_reminders.go):
//
//   1. Fully-qualified SPA paths — "/orgs/<slug>/settings/feedback/<id>"
//   2. Org-relative paths — "/pulse", "/assistant" — which need the current
//      org slug spliced in, and "/orgs/_/billing" where "_" is an explicit
//      slug placeholder.
//   3. Paths outside the org-scoped route tree — "/pay/<token>", "/n/<token>"
//      and friends — which must be left alone.
//
// Without this, an org-relative URL navigates to a path that matches no route
// in App.tsx and the user lands on a blank page.

// Top-level route segments that live OUTSIDE /orgs/:slug in App.tsx. Anything
// else that arrives as a single-segment absolute path is org-relative.
const TOP_LEVEL_SEGMENTS = new Set([
  'orgs',
  'auth',
  'pay',
  'store',
  'm',
  'n',
  'onboarding',
]);

// The slug placeholder some backend producers emit rather than doing a slug
// lookup they don't have the deps for.
const SLUG_PLACEHOLDER = '_';

export interface NotificationLink {
  /** Where to send the user. */
  href: string;
  /** True when href leaves the SPA (absolute URL) and needs a real navigation. */
  external: boolean;
}

/**
 * resolveNotificationLink turns a raw action_url into a navigable target, or
 * null when the URL can't be resolved (empty, or org-relative with no slug in
 * context). Returning null is deliberate: a click that does nothing beyond
 * marking the row read beats one that navigates to a blank page.
 */
export function resolveNotificationLink(
  actionURL: string | null | undefined,
  slug: string | null | undefined,
): NotificationLink | null {
  const raw = (actionURL ?? '').trim();
  if (!raw) return null;

  // Absolute URLs and protocol-relative ones leave the SPA as-is.
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//')) {
    return { href: raw, external: true };
  }

  const path = raw.startsWith('/') ? raw : `/${raw}`;
  // Split the leading path segment off any query string / fragment so
  // "/pulse?campaign=x" classifies the same way "/pulse" does.
  const boundary = path.search(/[?#]/);
  const pathname = boundary === -1 ? path : path.slice(0, boundary);
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0] ?? '';

  const org = (slug ?? '').trim();

  // "/orgs/_/billing" — substitute the placeholder with the live slug.
  if (first === 'orgs' && segments[1] === SLUG_PLACEHOLDER) {
    if (!org) return null;
    const rest = path.slice(`/orgs/${SLUG_PLACEHOLDER}`.length);
    return { href: `/orgs/${encodeURIComponent(org)}${rest}`, external: false };
  }

  // Already-qualified or deliberately org-less routes pass through untouched.
  if (TOP_LEVEL_SEGMENTS.has(first)) {
    return { href: path, external: false };
  }

  // Everything else is org-relative — "/pulse", "/assistant", "/billing".
  if (!org) return null;
  return { href: `/orgs/${encodeURIComponent(org)}${path}`, external: false };
}
